import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Tooltip,
  Pagination,
  useMediaQuery,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Stack
} from '@mui/material';
import WaterCard from './WaterCard';
import {
  Search as SearchIcon,
  Phone as PhoneIcon,
  Clear as ClearIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  Download as DownloadIcon,
  Dashboard as DashboardIcon,
  ConfirmationNumber as ConfirmationNumberIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { callsApi } from '../services/api';
import AdvancedFilter, { AdvancedFilterConfig } from './AdvancedFilter';
import CallSummaryCard from './CallSummaryCard';
import { OutcomeChip } from './AnalyticsDashboard';
import { exportCallsToCSV } from '../utils/csvExport';
import CustomerDetailDrawer from './CustomerDetailDrawer';

interface Call {
  call_id: string;
  customer_name?: string;
  caller_name?: string;
  customer_phone?: string;
  start_time?: string;
  duration?: number;
  duration_seconds?: number;
  message_count?: number;
  exchange_count?: number;
  issue_category?: string;
  issue_details?: string;
  urgency_level?: string;
  resolved?: boolean;
  callback_requested?: boolean;
  callback_status?: string;
  human_transfer_requested?: boolean;
  escalation_requested?: boolean;
  under_30_seconds?: boolean;
  status?: string;
  created_at?: string;
  transcript?: string;
  recording_url?: string;
  channel_type?: string;
  ticket_id?: number | string | null;
  outcome?: string;
}

const UnifiedCallDashboard: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [calls, setCalls] = useState<Call[]>([]);
  const [serverTotal, setServerTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [phoneSearchTerm, setPhoneSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'card' | 'table'>('table');
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilterConfig>({
    searchTerm: '',
    status: [],
    priority: [],
    category: [],
    dateRange: { start: '', end: '' },
    durationRange: [0, 3600],
    urgencyLevel: [],
    resolutionStatus: []
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [callsPerPage] = useState(10);
  const [customerDrawerOpen, setCustomerDrawerOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<string | null>(null);
  const [selectedCustomerData, setSelectedCustomerData] = useState<any>(null);

  useEffect(() => {
    fetchCalls();
    // Poll for updates every 30 seconds
    const interval = setInterval(fetchCalls, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchCalls = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await callsApi.getAllCalls({ limit: 1000, period: '30d' });
      // Handle both array and object response formats
      const callsData = response.data;
      setCalls(Array.isArray(callsData) ? callsData : (callsData?.calls || []));
      setServerTotal(typeof callsData?.total === 'number' ? callsData.total : null);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching calls:', err);
      setError('Failed to fetch call data');
      setLoading(false);
    }
  };

  const formatPhoneNumber = (phone?: string) => {
    if (!phone) return 'N/A';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    } else if (cleaned.length === 10) {
      return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const formatDuration = (call: Call) => {
    // For SMS/Chat, show exchange count if available
    if (call.channel_type === 'CHAT' && call.exchange_count !== undefined && call.exchange_count !== null) {
      const duration = call.duration || call.duration_seconds;
      if (duration && duration > 0) {
        const minutes = Math.floor(duration / 60);
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        
        let timeStr = '';
        if (hours > 0) {
          timeStr = `${hours}h ${remainingMinutes}m`;
        } else if (minutes > 0) {
          timeStr = `${minutes}m`;
        } else {
          timeStr = `${duration}s`;
        }
        
        return `${call.exchange_count} ${call.exchange_count === 1 ? 'exchange' : 'exchanges'} (${timeStr})`;
      }
      return `${call.exchange_count} ${call.exchange_count === 1 ? 'exchange' : 'exchanges'}`;
    }
    
    // For voice calls, show duration
    const duration = call.duration || call.duration_seconds;
    if (!duration || duration <= 0) return 'N/A';
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatDate = (timestamp?: string) => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp).toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const formatTime = (timestamp?: string) => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const filteredCalls = useMemo(() => {
    return calls.filter(call => {
      // Search term filter
      const searchLower = advancedFilters.searchTerm.toLowerCase();
      const matchesSearch = !searchLower || (
        call.call_id?.toLowerCase().includes(searchLower) ||
        call.customer_phone?.toLowerCase().includes(searchLower) ||
        call.status?.toLowerCase().includes(searchLower) ||
        call.transcript?.toLowerCase().includes(searchLower) ||
        call.customer_name?.toLowerCase().includes(searchLower) ||
        call.issue_category?.toLowerCase().includes(searchLower)
      );

      // Status filter
      const matchesStatus = advancedFilters.status.length === 0 || 
        advancedFilters.status.includes(call.status || '');

      // Priority filter
      const matchesPriority = advancedFilters.priority.length === 0 || 
        advancedFilters.priority.includes(call.urgency_level || '');

      // Category filter
      const matchesCategory = advancedFilters.category.length === 0 || 
        advancedFilters.category.includes(call.issue_category || '');

      // Urgency level filter
      const matchesUrgency = advancedFilters.urgencyLevel.length === 0 || 
        advancedFilters.urgencyLevel.includes(call.urgency_level || '');

      // Resolution status filter
      const isResolved = call.status === 'completed' || call.resolved;
      const matchesResolution = advancedFilters.resolutionStatus.length === 0 || 
        (advancedFilters.resolutionStatus.includes('Resolved') && isResolved) ||
        (advancedFilters.resolutionStatus.includes('Unresolved') && !isResolved);

      // Phone number search filter
      const phoneLower = phoneSearchTerm.toLowerCase();
      const matchesPhone = !phoneLower || (
        call.customer_phone?.toLowerCase().includes(phoneLower)
      );

      return matchesSearch && matchesStatus && matchesPriority && matchesCategory && 
             matchesUrgency && matchesResolution && matchesPhone;
    });
  }, [calls, advancedFilters, phoneSearchTerm]);

  // Pagination logic
  const paginatedCalls = filteredCalls.slice(
    (currentPage - 1) * callsPerPage,
    currentPage * callsPerPage
  );
  const totalPages = Math.ceil(filteredCalls.length / callsPerPage);

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [advancedFilters, phoneSearchTerm]);

  // Generate filter options from calls data
  const generateFilterOptions = () => {
    const statuses = Array.from(new Set(calls.map(call => call.status).filter(Boolean)));
    const priorities = Array.from(new Set(calls.map(call => call.urgency_level).filter(Boolean)));
    const categories = Array.from(new Set(calls.map(call => call.issue_category).filter(Boolean)));
    
    return {
      statuses: statuses.map(status => ({
        label: status || 'Unknown',
        value: status || 'Unknown',
        count: calls.filter(call => call.status === status).length,
        color: status === 'completed' ? '#2e7d32' : status === 'failed' ? '#d32f2f' : '#1976d2'
      })),
      priorities: priorities.map(priority => ({
        label: priority || 'Medium',
        value: priority || 'Medium',
        count: calls.filter(call => call.urgency_level === priority).length,
        color: priority === 'High' ? '#d32f2f' : priority === 'Low' ? '#2e7d32' : '#f57c00'
      })),
      categories: categories.map(category => ({
        label: category || 'General',
        value: category || 'General',
        count: calls.filter(call => call.issue_category === category).length
      })),
      urgencyLevels: [
        { label: 'Low', value: 'Low', count: calls.filter(call => call.urgency_level === 'Low').length, color: '#2e7d32' },
        { label: 'Medium', value: 'Medium', count: calls.filter(call => call.urgency_level === 'Medium').length, color: '#f57c00' },
        { label: 'High', value: 'High', count: calls.filter(call => call.urgency_level === 'High').length, color: '#d32f2f' }
      ],
      resolutionStatuses: [
        { label: 'Resolved', value: 'Resolved', count: calls.filter(call => 
          call.status === 'completed' || call.resolved
        ).length, color: '#2e7d32' },
        { label: 'Unresolved', value: 'Unresolved', count: calls.filter(call => 
          !(call.status === 'completed' || call.resolved)
        ).length, color: '#d32f2f' }
      ]
    };
  };

  const handleViewDetails = (callId: string) => {
    // Navigate to call detail page or open modal
    navigate(`/calls/${callId}`);
  };

  const handleCustomerClick = (event: React.MouseEvent, call: Call) => {
    event.stopPropagation(); // Prevent row click
    setSelectedCustomer(call.customer_phone || '');
    setSelectedCustomerData({
      name: call.customer_name || 'Unknown',
      phone: call.customer_phone,
      // Add more customer data as available
    });
    setCustomerDrawerOpen(true);
  };

  const handleCloseCustomerDrawer = () => {
    setCustomerDrawerOpen(false);
    setSelectedCustomer(null);
    setSelectedCustomerData(null);
  };

  if (loading && calls.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchCalls}>
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#F3F2F2', minHeight: '100vh', p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Advanced Search & Filter */}
      <AdvancedFilter
        onFilterChange={setAdvancedFilters}
        onSearchChange={setSearchTerm}
        searchTerm={searchTerm}
        availableOptions={generateFilterOptions()}
        showAdvanced={false}
      />

      {/* Quick Phone Number Search */}
      <WaterCard sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 3,
        mb: 3,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
          transform: 'translateY(-1px)'
        }
      }}>
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: { xs: 1.5, sm: 2, md: 3 } }}>
            <Box sx={{
              background: 'linear-gradient(135deg, #0176D3 0%, #005FB2 100%)',
              borderRadius: 2,
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <PhoneIcon sx={{ color: 'white', fontSize: { xs: 20, sm: 24 } }} />
            </Box>
            <Typography variant="h6" fontWeight="700" sx={{ color: '#080707', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Quick Phone Lookup
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => exportCallsToCSV(filteredCalls)}
              size="medium"
              sx={{ 
                mr: 1, 
                display: { xs: 'none', sm: 'flex' },
                borderRadius: 2,
                borderColor: '#0176D3',
                color: '#0176D3',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#005FB2',
                  backgroundColor: 'rgba(1, 118, 211, 0.04)'
                }
              }}
            >
              Export CSV
            </Button>
            <Tooltip title="Export to CSV">
              <IconButton
                onClick={() => exportCallsToCSV(filteredCalls)}
                size="small"
                sx={{ display: { xs: 'flex', sm: 'none' }, mr: 1 }}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="medium"
              sx={{
                '& .MuiToggleButton-root': {
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&.Mui-selected': {
                    backgroundColor: '#0176D3',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#005FB2'
                    }
                  }
                }
              }}
            >
              <ToggleButton value="card">
                <ViewModuleIcon sx={{ fontSize: 18, mr: 0.5 }} />
                Cards
              </ToggleButton>
              <ToggleButton value="table">
                <ViewListIcon sx={{ fontSize: 18, mr: 0.5 }} />
                Table
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <TextField
            fullWidth
            placeholder="Enter phone number to search calls..."
            value={phoneSearchTerm}
            onChange={(e) => setPhoneSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#706E6B', fontSize: { xs: 18, sm: 20 } }} />
                </InputAdornment>
              ),
              endAdornment: phoneSearchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setPhoneSearchTerm('')}>
                    <ClearIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'white',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#0176D3'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#0176D3',
                  borderWidth: '2px'
                }
              }
            }}
          />
          {phoneSearchTerm && (
            <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
              <Typography variant="body2" sx={{ color: '#666666', mb: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                Found {filteredCalls.length} calls for this number
              </Typography>
            </Box>
          )}
        </Box>
      </WaterCard>

      {/* Calls Display */}
      {viewMode === 'card' ? (
        <Grid container spacing={2}>
          {paginatedCalls.map((call) => (
            <Grid item xs={12} sm={6} md={4} key={call.call_id}>
              <CallSummaryCard call={call} onViewDetails={handleViewDetails} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <WaterCard
          shimmerDelay={1}
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 3,
            bgcolor: 'background.paper',
          }}
        >
          {/* Redesigned Header */}
          <Box 
            sx={{ 
              background: 'linear-gradient(135deg, #0176D3 0%, #005FB2 100%)', 
              p: 3.5,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Decorative elements */}
            <Box
              sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 120,
                height: 120,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 70%)',
              }}
            />
            <Stack direction="row" alignItems="center" justifyContent="space-between" position="relative">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: 'rgba(255,255,255,0.15)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  }}
                >
                  <DashboardIcon sx={{ color: 'white', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight="700" sx={{ color: 'white', mb: 0.5, textShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    Call Management Center
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.95)' }}>
                    {filteredCalls.length} active records • {serverTotal !== null && serverTotal > calls.length ? `showing latest ${calls.length} of ${serverTotal}` : `${calls.length} total`} • last 30 days
                  </Typography>
                </Box>
              </Stack>
              <Chip
                label="LIVE"
                sx={{
                  bgcolor: '#10B981',
                  color: 'white',
                  fontWeight: 800,
                  fontSize: '0.75rem',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
                  animation: 'pulse 2s infinite',
                }}
              />
            </Stack>
          </Box>

          <TableContainer sx={{ overflowX: 'auto', maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell 
                    sx={{ 
                      fontWeight: 800, 
                      color: '#1E293B', 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }, 
                      display: { xs: 'none', sm: 'table-cell' },
                      background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)',
                      borderBottom: '2px solid #CBD5E1',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      py: 2,
                    }}
                  >
                    📅 Date
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 800, 
                      color: '#1E293B', 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }, 
                      display: { xs: 'none', sm: 'table-cell' },
                      background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)',
                      borderBottom: '2px solid #CBD5E1',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      py: 2,
                    }}
                  >
                    🕐 Time
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 800, 
                      color: '#1E293B', 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)',
                      borderBottom: '2px solid #CBD5E1',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      py: 2,
                    }}
                  >
                    📞 Phone
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 800, 
                      color: '#1E293B', 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)',
                      borderBottom: '2px solid #CBD5E1',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      py: 2,
                    }}
                  >
                    👤 Account Holder
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 800, 
                      color: '#1E293B', 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }, 
                      display: { xs: 'none', md: 'table-cell' },
                      background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)',
                      borderBottom: '2px solid #CBD5E1',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      py: 2,
                    }}
                  >
                    ⏱️ Duration
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 800, 
                      color: '#1E293B', 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }, 
                      display: { xs: 'none', lg: 'table-cell' },
                      background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)',
                      borderBottom: '2px solid #CBD5E1',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      py: 2,
                    }}
                  >
                    📋 Category
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 800, 
                      color: '#1E293B', 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }, 
                      textAlign: 'center',
                      background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)',
                      borderBottom: '2px solid #CBD5E1',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      py: 2,
                    }}
                  >
                    🎯 Outcome
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 800,
                      color: '#1E293B',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      display: { xs: 'none', lg: 'table-cell' },
                      background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)',
                      borderBottom: '2px solid #CBD5E1',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      py: 2,
                    }}
                  >
                    🎫 Ticket
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCalls.map((call, index) => (
                  <TableRow 
                    key={call.call_id} 
                    hover 
                    onClick={() => handleViewDetails(call.call_id)}
                    sx={{ 
                      cursor: 'pointer',
                      backgroundColor: index % 2 === 0 ? 'white' : '#FAFBFC',
                      borderLeft: `3px solid transparent`,
                      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': { 
                        backgroundColor: 'rgba(59, 130, 246, 0.08)',
                        boxShadow: '0 2px 8px rgba(59, 130, 246, 0.15) inset',
                        borderLeft: `3px solid #3B82F6`,
                        '& .MuiTableCell-root': {
                          color: '#1E293B',
                        }
                      }
                    }}
                  >
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, py: 2.5 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600, 
                          color: '#475569', 
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          fontFamily: '"SF Mono", monospace',
                        }}
                      >
                        {formatDate(call.start_time || call.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, py: 2.5 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600, 
                          color: '#475569', 
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          fontFamily: '"SF Mono", monospace',
                        }}
                      >
                        {formatTime(call.start_time || call.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2.5 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 700, 
                          color: '#0F172A', 
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          fontFamily: '"SF Mono", monospace',
                        }}
                      >
                        {formatPhoneNumber(call.customer_phone)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2.5 }}>
                      <Box
                        onClick={(e) => handleCustomerClick(e, call)}
                        sx={{
                          cursor: 'pointer',
                          display: 'inline-block',
                          '&:hover': {
                            '& .customer-name': {
                              color: '#3B82F6',
                              textDecoration: 'underline',
                            }
                          }
                        }}
                      >
                        <Typography 
                          className="customer-name"
                          variant="body2" 
                          sx={{ 
                            fontWeight: 600, 
                            color: '#334155', 
                            fontSize: { xs: '0.75rem', sm: '0.875rem' }, 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            maxWidth: 200,
                            transition: 'all 0.2s',
                          }}
                        >
                          {call.customer_name || 'Unknown'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, py: 2.5 }}>
                      <Chip
                        label={formatDuration(call)}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          bgcolor: '#EFF6FF',
                          color: '#1E40AF',
                          fontFamily: '"SF Mono", monospace',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, py: 2.5 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 600, 
                            color: '#475569', 
                            fontSize: { xs: '0.75rem', sm: '0.875rem' } 
                          }}
                        >
                          {call.issue_category || 'N/A'}
                        </Typography>
                        {call.channel_type && (
                          <Chip
                            label={call.channel_type}
                            size="small"
                            sx={{
                              height: '22px',
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              background: call.channel_type === 'CHAT' 
                                ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)' 
                                : 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
                              color: 'white',
                              borderRadius: 1.5,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            }}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', py: 2.5 }}>
                      <OutcomeChip outcome={call.outcome || 'No Action'} />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, py: 2.5 }}>
                      {call.ticket_id ? (
                        <Chip
                          size="small"
                          icon={<ConfirmationNumberIcon />}
                          label={`#${call.ticket_id}`}
                          component="a"
                          href={`https://fongo.zendesk.com/agent/tickets/${call.ticket_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          clickable
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Enhanced Pagination Controls */}
          {filteredCalls.length > 0 && (
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                px: 3,
                py: 3, 
                borderTop: '1px solid rgba(0,0,0,0.08)', 
                background: 'linear-gradient(135deg, #F8FAFC 0%, #FFFFFF 100%)',
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#64748B', 
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  fontWeight: 600,
                }}
              >
                Showing <Box component="span" sx={{ fontWeight: 800, color: 'text.primary' }}>{((currentPage - 1) * callsPerPage) + 1}-{Math.min(currentPage * callsPerPage, filteredCalls.length)}</Box> of <Box component="span" sx={{ fontWeight: 800, color: 'text.primary' }}>{filteredCalls.length}</Box>
              </Typography>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? "small" : "medium"}
                showFirstButton={!isMobile}
                showLastButton={!isMobile}
                siblingCount={isMobile ? 0 : 1}
                boundaryCount={isMobile ? 1 : 1}
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontWeight: 600,
                    '&.Mui-selected': {
                      background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
                    },
                  },
                }}
              />
            </Box>
          )}

          <style>
            {`
              @keyframes pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.7; }
              }
            `}
          </style>
        </WaterCard>
      )}

      {/* Customer 360 Detail Drawer */}
      <CustomerDetailDrawer
        open={customerDrawerOpen}
        onClose={handleCloseCustomerDrawer}
        customerId={selectedCustomer}
        customerData={selectedCustomerData}
      />
    </Box>
  );
};

export default UnifiedCallDashboard;

